import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DownloadSimple, X, Flask } from "@phosphor-icons/react";

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already dismissed this session
    if (sessionStorage.getItem("pwa-dismissed")) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 3000);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowBanner(false);
    sessionStorage.setItem("pwa-dismissed", "true");
  };

  // Don't show if already installed as PWA
  if (window.matchMedia("(display-mode: standalone)").matches) return null;
  if (dismissed || !showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        className="fixed bottom-20 md:bottom-6 left-4 right-4 z-[70] max-w-md mx-auto"
        data-testid="pwa-install-banner"
      >
        <div className="rounded-2xl bg-[#2D6A4F] p-4 shadow-2xl shadow-[#2D6A4F]/30 border border-white/10">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
              <img src="/icon-192x192.png" alt="Mood Lab" className="w-8 h-8 rounded-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-['Outfit'] font-medium text-sm">
                Install Mood Chemistry Lab
              </p>
              <p className="text-white/60 text-xs mt-0.5">
                Add to your home screen for the full experience — offline access, faster loading, no app store needed.
              </p>
            </div>
            <button
              onClick={handleDismiss}
              className="shrink-0 p-1 text-white/40 hover:text-white/70 transition-colors"
              data-testid="dismiss-pwa-banner"
            >
              <X size={16} />
            </button>
          </div>
          <div className="flex gap-2 mt-3 ml-14">
            <button
              onClick={handleInstall}
              data-testid="pwa-install-button"
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-[#2D6A4F] text-xs font-semibold hover:bg-white/90 transition-all hover:-translate-y-0.5 shadow-md"
            >
              <DownloadSimple size={14} weight="bold" />
              Install App
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-2 rounded-full text-white/60 text-xs font-medium hover:text-white/80 transition-colors"
              data-testid="pwa-not-now"
            >
              Not now
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
